import {
    ChangeEvent,
    ComponentPropsWithRef,
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
} from 'react';
import { twMerge } from 'tailwind-merge';

interface AutoGrowHTMLTextAreaElement extends HTMLTextAreaElement {
    resetHeight: () => void;
}

export interface AutoGrowTextareaProps extends ComponentPropsWithRef<'textarea'> {
    ref?: ForwardedRef<HTMLTextAreaElement>;
}

const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>((props, ref) => {
    const { onChange, className, rows = 1, ...rest } = props;

    const initialHeightRef = useRef<number>(0);

    const internalRef = useRef<AutoGrowHTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => internalRef.current, [internalRef]);

    const resetHeight = useCallback(() => {
        internalRef.current.style.height = initialHeightRef.current + 'px';
    }, []);

    useEffect(() => {
        const _initialHeight = internalRef.current.scrollHeight + 2;
        initialHeightRef.current = _initialHeight;

        internalRef.current.resetHeight = resetHeight;
    }, [resetHeight]);

    const handleAutoGrow = useCallback(() => {
        resetHeight(); // Reinitialize everytime so that whenever the user deletes text, textarea shrinks

        internalRef.current.style.height = internalRef.current.scrollHeight + 2 + 'px';
    }, [resetHeight]);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            onChange?.(e);

            handleAutoGrow();
        },
        [handleAutoGrow, onChange]
    );

    return (
        <textarea
            {...rest}
            ref={internalRef}
            onChange={handleChange}
            className={twMerge('resize-none', className)}
            rows={rows}
        />
    );
});

export default AutoGrowTextarea;
